// Real-time calming soundscape synthesis using the Web Audio API.
// Everything is generated live (no external audio files), so nothing can ever fail to load.
// Each preset is a set of continuous layers + scheduled event layers.

const PRESETS = {
  ocean:      { layers: [{ kind: "noise", color: "white", filter: "lowpass", freq: 650, hp: 180, gain: 0.55, trem: { rate: 0.12, depth: 0.45 } }] },
  rain:       { layers: [{ kind: "noise", color: "white", filter: "bandpass", freq: 1400, Q: 0.6, hp: 500, gain: 0.5 }] },
  hearth:     { layers: [{ kind: "noise", color: "brown", filter: "lowpass", freq: 500, gain: 0.5 }],
                events: [{ kind: "noiseBurst", color: "white", filterType: "bandpass", freqMin: 900, freqMax: 2400, decay: 0.08, gain: 0.4, min: 90, max: 340 }] },
  bowl:       { events: [{ kind: "tonePing", notes: [288], partials: true, decay: 6, gain: 0.2, min: 7000, max: 7000 }] },
  stream:     { layers: [{ kind: "noise", color: "white", filter: "bandpass", freq: 900, Q: 0.7, hp: 420, gain: 0.45 }],
                events: [{ kind: "tonePing", notes: [1600, 1900, 2200], decay: 0.12, gain: 0.08, min: 120, max: 520 }] },
  wind:       { layers: [
                  { kind: "noise", color: "brown", filter: "lowpass", freq: 420, gain: 0.5, trem: { rate: 0.07, depth: 0.6 } },
                  { kind: "noise", color: "white", filter: "bandpass", freq: 950, Q: 0.5, hp: 600, gain: 0.16, trem: { rate: 0.13, depth: 0.78 } },
                ] },
  forest:     { layers: [{ kind: "noise", color: "brown", filter: "lowpass", freq: 480, gain: 0.12, trem: { rate: 0.1, depth: 0.4 } }],
                events: [
                  { kind: "bird", notes: [2000, 2300, 2600, 3000, 3400], gain: 0.17, min: 450, max: 1700 },
                  { kind: "bird", notes: [1700, 1900, 2200], gain: 0.11, min: 900, max: 2600 },
                ] },
  night:      { layers: [{ kind: "noise", color: "brown", filter: "lowpass", freq: 260, gain: 0.1 }],
                events: [
                  { kind: "trill", freq: 4700, trillRate: 27, dur: 0.3, gain: 0.07, min: 180, max: 430 },
                  { kind: "trill", freq: 5200, trillRate: 33, dur: 0.22, gain: 0.05, min: 320, max: 720 },
                ] },
  thunder:    { layers: [{ kind: "noise", color: "white", filter: "bandpass", freq: 1300, Q: 0.6, hp: 480, gain: 0.45 }],
                events: [{ kind: "noiseBurst", color: "brown", filterType: "lowpass", freqMin: 90, freqMax: 150, decay: 2.2, gain: 0.5, min: 9000, max: 22000 }] },
  chimes:     { events: [{ kind: "tonePing", notes: [523, 587, 659, 784, 880], partials: true, decay: 3.2, gain: 0.12, min: 1500, max: 4200 }] },
  heartbeat:  { events: [{ kind: "thump", freq: 60, decay: 0.26, gain: 0.5, double: true, min: 1050, max: 1050 }] },
  waves432:   { layers: [{ kind: "tone", freqs: [432, 216, 648], wave: "sine", gain: 0.12, trem: { rate: 0.09, depth: 0.5 } }] },
  meadow:     { layers: [{ kind: "noise", color: "brown", filter: "lowpass", freq: 480, gain: 0.26, trem: { rate: 0.09, depth: 0.4 } }],
                events: [{ kind: "tonePing", notes: [2000, 2400, 2800, 3200], glide: 4, decay: 0.16, gain: 0.08, min: 900, max: 3000 }] },
  rainforest: { layers: [{ kind: "noise", color: "white", filter: "bandpass", freq: 1500, Q: 0.6, hp: 520, gain: 0.4 }],
                events: [
                  { kind: "tonePing", notes: [1900, 2300, 2700], glide: 5, decay: 0.18, gain: 0.08, min: 1100, max: 3400 },
                  { kind: "tonePing", notes: [4200, 4600], decay: 0.03, gain: 0.04, min: 180, max: 600 },
                ] },
  drone:      { layers: [{ kind: "tone", freqs: [110, 55, 165], wave: "sine", gain: 0.14, trem: { rate: 0.06, depth: 0.3 } }] },
  sleep:      { layers: [{ kind: "noise", color: "white", filter: "lowpass", freq: 1200, gain: 0.5 }] },
};

export class SanctuaryEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.analyser = null;
    this.nodes = [];
    this.timers = [];
    this.current = null;
  }

  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.6;
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 128;
    this.master.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
  }

  _noiseBuffer(type = "white") {
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      if (type === "brown") { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.5; }
      else d[i] = w;
    }
    return buf;
  }

  setVolume(v) { this._ensure(); this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1); }

  stop() {
    this.nodes.forEach((n) => { try { n.stop ? n.stop() : n.disconnect(); } catch (e) {} });
    this.timers.forEach((t) => clearInterval(t));
    this.nodes = [];
    this.timers = [];
    this.current = null;
  }

  pauseSilent() { this.silentEl?.pause(); }

  _buildContinuous(l, t) {
    let src;
    if (l.kind === "noise") {
      src = this.ctx.createBufferSource();
      src.buffer = this._noiseBuffer(l.color);
      src.loop = true;
      let node = src;
      if (l.hp) { const hp = this.ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = l.hp; node.connect(hp); node = hp; }
      if (l.filter) { const f = this.ctx.createBiquadFilter(); f.type = l.filter; f.frequency.value = l.freq; if (l.Q) f.Q.value = l.Q; node.connect(f); node = f; }
      const g = this.ctx.createGain(); g.gain.value = l.gain; node.connect(g).connect(this.master);
      if (l.trem) this._tremolo(g, l.trem, l.gain, t);
      src.start(t); this.nodes.push(src);
    } else if (l.kind === "tone") {
      l.freqs.forEach((f, i) => {
        const osc = this.ctx.createOscillator(); osc.type = l.wave || "sine"; osc.frequency.value = f;
        const g = this.ctx.createGain(); g.gain.value = l.gain / (i + 1);
        osc.connect(g).connect(this.master);
        if (l.trem) this._tremolo(g, l.trem, l.gain / (i + 1), t);
        osc.start(t); this.nodes.push(osc);
      });
    }
  }

  _tremolo(gainNode, trem, base, t) {
    const lfo = this.ctx.createOscillator(); lfo.frequency.value = trem.rate;
    const lg = this.ctx.createGain(); lg.gain.value = base * trem.depth;
    lfo.connect(lg).connect(gainNode.gain); lfo.start(t); this.nodes.push(lfo);
  }

  _scheduleEvent(ev) {
    const fire = () => {
      if (this.current == null) return;
      const now = this.ctx.currentTime;
      if (ev.kind === "tonePing") {
        const base = ev.notes[Math.floor(Math.random() * ev.notes.length)];
        const parts = ev.partials ? [1, 2.76, 5.4] : [1];
        parts.forEach((mult, i) => {
          const osc = this.ctx.createOscillator(); osc.type = ev.wave || "sine";
          osc.frequency.setValueAtTime(base * mult, now);
          if (ev.glide) osc.frequency.linearRampToValueAtTime(base * mult * Math.pow(2, ev.glide / 12), now + ev.decay * 0.6);
          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(ev.gain / (i + 1), now + 0.012);
          g.gain.exponentialRampToValueAtTime(0.0008, now + ev.decay);
          osc.connect(g).connect(this.master);
          osc.start(now); osc.stop(now + ev.decay + 0.1);
        });
      } else if (ev.kind === "noiseBurst") {
        const s = this.ctx.createBufferSource(); s.buffer = this._noiseBuffer(ev.color);
        const f = this.ctx.createBiquadFilter(); f.type = ev.filterType;
        f.frequency.value = ev.freqMin + Math.random() * (ev.freqMax - ev.freqMin);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(ev.gain * (0.6 + Math.random() * 0.4), now);
        g.gain.exponentialRampToValueAtTime(0.0008, now + ev.decay);
        s.connect(f).connect(g).connect(this.master);
        s.start(now); s.stop(now + ev.decay + 0.05);
      } else if (ev.kind === "thump") {
        const beat = (at, amp) => {
          const osc = this.ctx.createOscillator(); osc.type = "sine"; osc.frequency.value = ev.freq;
          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(ev.gain * amp, at + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0008, at + ev.decay);
          osc.connect(g).connect(this.master); osc.start(at); osc.stop(at + ev.decay + 0.05);
        };
        beat(now, 1); if (ev.double) beat(now + 0.28, 0.7);
      } else if (ev.kind === "bird") {
        // A short warble: 2-4 quick pitch-sweeping chirps — recognizable birdsong.
        const count = 2 + Math.floor(Math.random() * 3);
        let at = now;
        for (let k = 0; k < count; k++) {
          const base = ev.notes[Math.floor(Math.random() * ev.notes.length)];
          const dir = Math.random() > 0.5 ? 1 : -1;
          const osc = this.ctx.createOscillator(); osc.type = "sine";
          osc.frequency.setValueAtTime(base, at);
          osc.frequency.linearRampToValueAtTime(base * Math.pow(2, (dir * 4) / 12), at + 0.07);
          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0, at);
          g.gain.linearRampToValueAtTime(ev.gain, at + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0008, at + 0.11);
          osc.connect(g).connect(this.master);
          osc.start(at); osc.stop(at + 0.14);
          at += 0.08 + Math.random() * 0.06;
        }
      } else if (ev.kind === "trill") {
        // A rhythmic, buzzy cricket chirp: a high tone gated by a fast square LFO.
        const osc = this.ctx.createOscillator(); osc.type = "triangle";
        osc.frequency.value = ev.freq * (0.97 + Math.random() * 0.06);
        const g = this.ctx.createGain();
        const dur = ev.dur;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(ev.gain, now + 0.02);
        g.gain.setValueAtTime(ev.gain, now + dur - 0.04);
        g.gain.linearRampToValueAtTime(0, now + dur);
        const lfo = this.ctx.createOscillator(); lfo.type = "square"; lfo.frequency.value = ev.trillRate;
        const lg = this.ctx.createGain(); lg.gain.value = ev.gain * 0.9;
        lfo.connect(lg).connect(g.gain);
        osc.connect(g).connect(this.master);
        osc.start(now); osc.stop(now + dur + 0.05);
        lfo.start(now); lfo.stop(now + dur + 0.05);
      }
    };
    const loop = () => {
      const delay = ev.min + Math.random() * (ev.max - ev.min);
      const id = setTimeout(() => { fire(); if (this.current != null) loop(); }, delay);
      this.timers.push(id);
    };
    fire(); loop();
  }

  // iPhone/iPad: let sounds play even when the ringer/silent switch is on.
  _unlockIOS() {
    if (navigator.audioSession) navigator.audioSession.type = "playback";
    if (this.silentEl) { this.silentEl.play().catch(() => {}); return; }
    const sr = 8000, n = sr / 2, buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
    const str = (o, t) => [...t].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
    str(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); str(8, "WAVEfmt "); v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true);
    v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, n * 2, true);
    this.silentEl = new Audio(URL.createObjectURL(new Blob([buf], { type: "audio/wav" })));
    this.silentEl.loop = true;
    this.silentEl.setAttribute("playsinline", "");
    this.silentEl.play().catch(() => {});
  }

  async play(kind) {
    this._ensure();
    this._unlockIOS();
    if (this.ctx.state === "suspended") await this.ctx.resume();
    this.stop();
    this.current = kind;
    const preset = PRESETS[kind];
    if (!preset) return;
    const t = this.ctx.currentTime;
    (preset.layers || []).forEach((l) => this._buildContinuous(l, t));
    (preset.events || []).forEach((ev) => this._scheduleEvent(ev));
  }
}
